import React, { useEffect, useRef, useCallback } from 'react';
import './Modal.css';

/**
 * Accessible modal dialog.
 *
 * - role="dialog" aria-modal="true"
 * - Escape closes
 * - Click on overlay closes
 * - Focus is trapped inside the dialog while open
 * - On open: focus moves to the first focusable element (or the dialog container)
 * - On close: focus is restored to the element that had focus before opening
 * - aria-labelledby points to the dialog title when `titleId` is provided
 *
 * Render `<Modal onClose={...}>...</Modal>` (or return null yourself when closed).
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const Modal = ({ onClose, titleId, children, className = '', dialogClassName = '', labelledBy }) => {
  const dialogRef = useRef(null);
  const overlayRef = useRef(null);
  const restoreFocusRef = useRef(null);

  // On open: record the active element so we can restore focus on close.
  useEffect(() => {
    restoreFocusRef.current = document.activeElement;
    return () => {
      if (restoreFocusRef.current && typeof restoreFocusRef.current.focus === 'function') {
        try { restoreFocusRef.current.focus(); } catch (_) { /* element may have unmounted */ }
      }
    };
  }, []);

  // On open: focus the first focusable element inside the dialog (or the dialog itself).
  useEffect(() => {
    const node = dialogRef.current;
    if (!node) return;
    const focusables = node.querySelectorAll(FOCUSABLE_SELECTOR);
    const first = focusables[0];
    if (first) {
      first.focus();
    } else {
      // Make the dialog container itself focusable so Escape still works.
      node.setAttribute('tabindex', '-1');
      node.focus();
    }
  }, []);

  // Escape closes the modal at the document level — the overlay div's own
  // onKeyDown only fires when focus is inside the overlay, so a user who
  // tabs to the body (or opens the dialog with focus on body) couldn't
  // Escape-close it before.
  // Stacked modals: every open Modal has a document listener, so without
  // the topmost-check below a single Escape would close ALL stacked
  // dialogs at once. Only the last overlay in the DOM acts on Escape.
  useEffect(() => {
    const onDocKeyDown = (e) => {
      if (e.key === 'Escape') {
        const overlays = document.querySelectorAll('.modal-overlay');
        if (overlays.length > 0 && overlays[overlays.length - 1] !== overlayRef.current) return;
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onDocKeyDown);
    return () => document.removeEventListener('keydown', onDocKeyDown);
  }, [onClose]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Tab') {
      const node = dialogRef.current;
      if (!node) return;
      const focusables = Array.from(node.querySelectorAll(FOCUSABLE_SELECTOR));
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || !node.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  }, []);

  return (
    <div
      ref={overlayRef}
      className={`modal-overlay ${className}`}
      onClick={onClose}
      onKeyDown={handleKeyDown}
      role="presentation"
    >
      <div
        ref={dialogRef}
        className={`modal-dialog ${dialogClassName}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId || labelledBy || undefined}
      >
        {children}
      </div>
    </div>
  );
};

export default Modal;